<template>
  <div class="row">
    <div class="col-xl-6 d-flex">
      <div class="card flex-fill">
        <div class="card-header">
          <div
            class="d-flex align-items-center justify-content-between flex-wrap row-gap-2"
          >
            <h5>Leads By Companies</h5>
            <small class="text-muted">Click to filter</small>
          </div>
        </div>

        <div class="card-body">
          <transition name="fade-slide" mode="out-in">
            <div v-if="loading" class="p-2" key="skel_companies">
              <div class="skel skel-line skel-gap-12" style="width: 55%"></div>
              <div class="skel skel-line skel-gap-12" style="width: 80%"></div>
              <div class="skel skel-line skel-gap-12" style="width: 72%"></div>
              <div class="skel skel-line" style="width: 68%"></div>
            </div>

            <div v-else>
              <div v-if="!companies.length" class="text-muted small">
                No company summary for selected filters.
              </div>

              <div
                v-for="c in companies"
                :key="c.company_name"
                class="border border-dashed bg-transparent-light rounded p-2 mb-2 hover-pointer"
                @click="drillCompany(c.company_name)"
              >
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center">
                    <a
                      href="javascript:void(0);"
                      class="avatar avatar-md rounded-circle bg-gray-100 flex-shrink-0 me-2"
                    >
                      <span
                        class="d-flex align-items-center justify-content-center w-100 h-100"
                      >
                        <i class="ti ti-building"></i>
                      </span>
                    </a>
                    <div>
                      <h6 class="fw-medium mb-1">
                        {{ c.company_name || "—" }}
                      </h6>
                      <p class="text-truncate mb-0 text-muted">
                        {{ fmtNum(c.count) }} leads
                      </p>
                    </div>
                  </div>
                  <div>
                    <h6 class="mb-0">{{ fmtMoney(c.value, currency) }}</h6>
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>

    <div class="col-xl-6 d-flex">
      <div class="card flex-fill">
        <div class="card-header">
          <div
            class="d-flex align-items-center justify-content-between flex-wrap row-gap-2"
          >
            <h5>Leads by Source</h5>
            <small class="text-muted">Auto-updates with filters</small>
          </div>
        </div>

        <div class="card-body">
          <transition name="fade-slide" mode="out-in">
            <div v-if="loading" class="p-2" key="skel_donut">
              <div class="skel skel-chart" style="height: 250px"></div>
              <div class="mt-3">
                <div class="skel skel-line skel-gap-8" style="width: 60%"></div>
                <div class="skel skel-line skel-gap-8" style="width: 52%"></div>
                <div class="skel skel-line" style="width: 48%"></div>
              </div>
            </div>

            <div v-else key="content_donut">
              <div v-if="!donutSeries.length" class="text-muted small">
                No source breakdown for selected filters.
              </div>

              <div v-else id="donut-chart-leads">
                <apexchart
                  type="donut"
                  height="250"
                  :options="donutOptions"
                  :series="donutSeries"
                />
              </div>

              <div class="mt-3" v-if="donutLegend.length">
                <h6 class="mb-3">Breakdown</h6>
                <div
                  v-for="row in donutLegend"
                  :key="row.label"
                  class="d-flex align-items-center justify-content-between mb-2"
                >
                  <p class="f-13 mb-0">
                    <i class="ti ti-circle-filled text-secondary me-1"></i
                    >{{ row.label }}
                  </p>
                  <p class="f-13 fw-medium text-gray-9">
                    {{ fmtNum(row.count) }}
                  </p>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useLeadDashboardStore } from "@/stores/leadDashboardStore";

const dash = useLeadDashboardStore();

const loading = computed(() => dash.loading);
const currency = computed(() => dash.kpis?.currency || "KES");

const companies = computed(() =>
  Array.isArray(dash.topCompanies) ? dash.topCompanies : []
);

const sources = computed(() =>
  Array.isArray(dash.leadsBySource) ? dash.leadsBySource : []
);
const donutSeries = computed(() =>
  sources.value.map((x) => Number(x.count || 0))
);
const donutLabels = computed(() =>
  sources.value.map((x) => x.source || "Unknown")
);
const donutLegend = computed(() =>
  sources.value.map((x) => ({
    label: x.source || "Unknown",
    count: x.count || 0,
  }))
);

const donutOptions = computed(() => ({
  chart: { type: "donut", height: 250 },
  labels: donutLabels.value,
  legend: { show: false },
  plotOptions: { pie: { donut: { size: "70%" } } },
}));

async function drillCompany(company) {
  if (!company) return;
  dash.filters.q = company;
  await dash.fetchDashboard();
}

function fmtNum(v) {
  return Number(v || 0).toLocaleString();
}
function fmtMoney(v, cur = "KES") {
  const n = Number(v || 0);
  return `${cur} ${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
</script>

<style scoped>
.hover-pointer {
  cursor: pointer;
}
</style>
